<template>
  <pro-list-frame
    ref="plTable"
    :title="typeProp ? '' : $t($route.name)"
    icon="mdi-receipt-text-outline"
    :total="totalItems"
    :rows="items.length"
    :search.sync="search"
    :columns="toggleableHeaders"
    :hidden="hiddenColumns"
    :selected-count="selected.length"
    :loading="loading"
    :cell-sel="cellSel"
    :create-label="invoiceCanCreate ? $t(type) : ''"
    :fixed="isDesktopListViewport"
    @refresh="getItems"
    @filter="filter = true"
    @print="printStudio = true"
    @create="goTo(type)"
    @toggle-column="toggleColumn"
    @show-all-columns="hiddenColumns = []"
    @cell-down="onCellDown"
    @cell-over="onCellOver"
    @clear-cells="clearCellSel"
    @clear-selection="selected = []"
  >
    <template #tools>
      <export-excel
        v-if="type !== 'removedInvoices'"
        ref="refExcel"
        :data="excelItems"
        worksheet="abriron.com"
        name="data-ai.xls"
        type="xls"
        :title="$store.state.abrName"
        :total-prop="pageCount"
        :page-prop="excelPage"
        @getAllRows="getAllRows"
      >
        <button type="button" class="pl-btn pl-btn--icon" :title="$t('excel')">
          <v-icon size="16" color="#2E7D32">mdi-file-excel-outline</v-icon>
        </button>
      </export-excel>
      <button
        type="button"
        class="pl-btn"
        :title="$t('invoice-count')"
        :disabled="loading"
        @click="fetchCheckAverageFirst"
      >
        <my-icon :name="'check-average'" :width="16" :height="16" :cursor-pointer="true" />
        <span v-if="selectedCheck.length" class="pl-num">{{ faNum(selectedCheck.length) }}</span>
      </button>
      <v-menu offset-y left>
        <template v-slot:activator="{ attrs, on }">
          <button type="button" class="pl-btn pl-btn--icon" v-bind="attrs" v-on="on">
            <v-icon size="16">mdi-dots-vertical</v-icon>
          </button>
        </template>
        <v-list dense>
          <v-list-item
            v-if="$store.state.permissions && $store.state.permissions.includes('importExcelInvoice')"
            link
            @click="openUploadDialog"
          >
            <v-list-item-icon><v-icon>mdi-database-import-outline</v-icon></v-list-item-icon>
            <v-list-item-title v-text="$t('importData')" />
          </v-list-item>
          <v-list-item
            v-show="
              type === 'sell' &&
              $store.state.permissions &&
              $store.state.permissions.includes('reviewDigikalaOrder') &&
              digikalaPermission
            "
            :disabled="!selected || selected.length === 0"
            link
            @click="reviewDigikalaOrder"
          >
            <v-list-item-icon><v-icon>mdi-shopping-search-outline</v-icon></v-list-item-icon>
            <v-list-item-title v-text="$t('reviewDigikalaOrder')" />
          </v-list-item>
          <v-list-item
            v-show="type === 'removedInvoices'"
            :disabled="!selected || selected.length === 0"
            link
            @click="refreshRemovedItem"
          >
            <v-list-item-icon><v-icon>mdi-delete-restore</v-icon></v-list-item-icon>
            <v-list-item-title v-text="$t('refreshRemovedItem')" />
          </v-list-item>
          <v-list-item
            v-show="$route.name === 'sellTaxPayer' && taxPayerSystem"
            :disabled="!selected || selected.length === 0"
            link
            @click="sendTaxInvoice('review')"
          >
            <v-list-item-icon><v-icon>mdi-file-document-refresh-outline</v-icon></v-list-item-icon>
            <v-list-item-title v-text="$t('reviewInvoices')" />
          </v-list-item>
          <v-list-item link @click="barcode">
            <v-list-item-icon><v-icon>mdi-barcode</v-icon></v-list-item-icon>
            <v-list-item-title v-text="$t('productBarcode')" />
          </v-list-item>
          <v-list-item
            v-if="taxPayerSystem && (type == 'sell' || type == 'returnSell')"
            link
            :disabled="loading"
            @click="taxPayerConfirmation"
          >
            <v-list-item-icon><v-icon>mdi-cash</v-icon></v-list-item-icon>
            <v-list-item-title class="d-flex align-center">
              <span>{{ $t("send to tax payer system") }}</span>
              <v-chip
                v-if="taxPayerSelectedItems && taxPayerSelectedItems.length"
                x-small
                dark
                color="#0D47A1"
                class="mr-2 px-1 tax-payer-count-badge"
              >
                {{ taxPayerSelectedItems.length }}
              </v-chip>
            </v-list-item-title>
          </v-list-item>
          <log
            v-if="$store.state.permissions && $store.state.permissions.includes('logList')"
            ref="logRef"
            :type-prop="type"
            :listProp="true"
            mode-prop="button"
          />
        </v-list>
      </v-menu>
    </template>

    <template #kpis>
      <div class="pl-kpi pl-kpi--blue">
        <span class="pl-kpi__icon"><v-icon size="17">mdi-chart-bar</v-icon></span>
        <div class="pl-kpi__body">
          <span class="pl-kpi__label">{{ $t("sumNetAmount") }}</span>
          <span class="pl-kpi__value pl-num">{{ faNum($numberFormat(summary ? summary.amount : 0)) }}</span>
        </div>
      </div>
      <div class="pl-kpi pl-kpi--rose">
        <span class="pl-kpi__icon"><v-icon size="17">mdi-tag</v-icon></span>
        <div class="pl-kpi__body">
          <span class="pl-kpi__label">{{ $t("sumDiscount") }}</span>
          <span class="pl-kpi__value pl-num">{{ faNum($numberFormat(summary ? summary.discount : 0)) }}</span>
        </div>
      </div>
      <div v-if="hasCustomerClub" class="pl-kpi pl-kpi--green">
        <span class="pl-kpi__icon"><v-icon size="17">mdi-star</v-icon></span>
        <div class="pl-kpi__body">
          <span class="pl-kpi__label">{{ $t("club-credit") }}</span>
          <span class="pl-kpi__value pl-num">{{ faNum($numberFormat(summary ? summary.credit : 0)) }}</span>
        </div>
      </div>
      <div class="pl-kpi pl-kpi--amber">
        <span class="pl-kpi__icon"><v-icon size="17">mdi-percent</v-icon></span>
        <div class="pl-kpi__body">
          <span class="pl-kpi__label">{{ $t("sumTax") }}</span>
          <span class="pl-kpi__value pl-num">{{ faNum($numberFormat(summary ? summary.tax : 0)) }}</span>
        </div>
      </div>
      <div class="pl-kpi pl-kpi--teal">
        <span class="pl-kpi__icon"><v-icon size="17">mdi-wallet</v-icon></span>
        <div class="pl-kpi__body">
          <span class="pl-kpi__label">{{ $t("sumClearAmount") }}</span>
          <span class="pl-kpi__value pl-num">{{ faNum($numberFormat(summary ? summary.net_amount : 0)) }}</span>
        </div>
      </div>
    </template>

    <template #bulk>
      <button
        v-if="type !== 'removedInvoices'"
        type="button"
        class="pl-bulk__btn"
        @click="groupPrintInvoices"
      >
        <v-icon size="17">mdi-printer-settings</v-icon>
        {{ $t("groupPrint") }}
      </button>
      <template v-if="lockPermission">
        <button type="button" class="pl-bulk__btn" @click="lockGroup('lock')">
          <v-icon size="17">mdi-lock-outline</v-icon>
          {{ $t("lock") }}
        </button>
        <button type="button" class="pl-bulk__btn" @click="unlockGroup('unlock')">
          <v-icon size="17">mdi-lock-open-outline</v-icon>
          {{ $t("unlock") }}
        </button>
      </template>
      <button
        v-if="
          type !== 'removedInvoices' &&
          $store.state.permissions &&
          $store.state.permissions.includes('remove' + type[0].toUpperCase() + type.substring(1))
        "
        type="button"
        class="pl-bulk__btn pl-bulk__btn--danger"
        @click="openDeleteDialog(null, 'delete')"
      >
        <v-icon size="17">mdi-trash-can-outline</v-icon>
        {{ $t("remove") }}
      </button>
    </template>

      <v-data-table
        v-if="headers"
        ref="listTable"
        v-model="selected"
        v-column-resize="invoiceListColumnPlugins"
        v-column-drag="invoiceListColumnPlugins"
        v-column-pin="invoiceListColumnPlugins"
        :height="isDesktopListViewport ? plTableH : 323"
        fixed-header
        :headers="tableHeaders"
        :items="items"
        :options.sync="options"
        :page.sync="page"
        :server-items-length="totalItems"
        :loading="false"
        multi-sort
        dense
        :show-select="showSelect"
        item-key="id"
        :hide-default-header="isMobile || mobileShow"
        class="invoice-list-table"
        :footer-props="plFooterProps"
      >
        <template v-if="isMobile" v-slot:item="{ item, index }">
          <tr>
            <td class="px-0">
              <v-list-item
                class="data-table-list-items px-0"
                :class="{
                  'mobile-td-list-border-green':
                    item.type == 'sell' || item.type == 'presale' ? true : false,
                  'mobile-td-list-border-red': item.type == 'purchase' ? true : false,
                  'mobile-td-list-border-primary':
                    item.type == 'returnSell' || item.type == 'returnPurchase'
                      ? true
                      : false,
                }"
                dense
              >
                <v-list-item-icon class="ps-2 me-1">
                  <div class="d-flex">
                    <p class="mt-1">
                      {{ +index + +1 }}
                    </p>
                    <v-menu offset-y>
                      <template v-slot:activator="{ attrs, on }">
                        <v-btn color="primary" v-bind="attrs" icon v-on="on">
                          <v-icon medium> mdi-dots-vertical </v-icon>
                        </v-btn>
                      </template>

                      <v-list dense>
                        <v-list-item
                          link
                          :disabled="item.lock ? true : false"
                          @click="edit(item)"
                        >
                          <v-list-item-icon>
                            <v-icon>mdi-clipboard-edit</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title v-text="$t('edit')" />
                        </v-list-item>
                        <v-list-item link @click="viewInvoice(item)">
                          <v-list-item-icon>
                            <v-icon>mdi-eye-outline</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title v-text="$t('invoiceViewOnly')" />
                        </v-list-item>
                        <v-list-item link @click="printChoice(item)">
                          <v-list-item-icon>
                            <v-icon>mdi-printer-outline</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title v-text="$t('print')" />
                        </v-list-item>
                        <v-list-item v-if="type == 'sell'" link @click="invoiceSms(item)">
                          <v-list-item-icon>
                            <v-icon>mdi-message-text-outline</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title v-text="$t('sendInvoiceSMS')" />
                        </v-list-item>
                        <v-list-item
                          v-if="item.entrepot"
                          link
                          @click="entrepotPrint(item)"
                        >
                          <v-list-item-icon>
                            <v-icon>mdi-warehouse</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title v-text="$t(item.entrepot.type + 'Print')" />
                        </v-list-item>
                        <v-list-item link @click="copyInvoice(item)">
                          <v-list-item-icon>
                            <v-icon>mdi-content-copy</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title v-text="$t('invoiceCopy')" />
                        </v-list-item>
                        <v-list-item
                          v-clipboard:copy="$baseURL + '/' + type + '/edit/' + item.uid"
                          link
                        >
                          <v-list-item-icon>
                            <v-icon>mdi-link</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title v-text="$t('copy-link')" />
                        </v-list-item>
                        <v-list-item
                          v-show="
                            removePermission(type) && resolveDeleteBtnVisibility(item)
                          "
                          link
                          :disabled="item.lock ? true : false"
                          @click="openDeleteDialog(item, 'delete')"
                        >
                          <v-list-item-icon>
                            <v-icon color="pink"> mdi-trash-can-outline </v-icon>
                          </v-list-item-icon>
                          <v-list-item-title v-text="$t('remove')" />
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </div>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="text-wrap">
                    {{ item.customer ? item.customer.name : "" }} ({{ item.number }})
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-wrap">
                    {{ $t("document-number") }} :
                    {{ item.doc_header ? item.doc_header.number : "" }} | {{ $t("id") }} :
                    {{ item.id }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle class="text-wrap">
                    {{ $t("discount") }} :
                    {{ $numberFormat(item.discount ? item.discount : 0) }} |
                    {{ $t("tax") }} : {{ $numberFormat(item.tax ? item.tax : 0) }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle class="text-wrap">
                    {{ $t("date") }} : {{ item.date }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle class="text-wrap">
                    <div class="pt-1">
                      <v-chip
                        v-for="(tag, index) in item.tags"
                        :key="index"
                        :color="tag.color"
                        label
                        small
                        style="margin-left: 1px; margin-right: 1px"
                        class="px-1 mb-1"
                      >
                        <span class="px-1 white--text">
                          {{ tag.text }}
                        </span>
                      </v-chip>
                      <v-chip
                        v-for="(integrate, index) in item.integrator"
                        :key="index"
                        color="green"
                        label
                        small
                        style="margin-left: 1px; margin-right: 1px"
                        class="px-1 mb-1"
                      >
                        <span class="px-1 white--text">
                          {{ $t("registerBy") }} : {{ $t(integrate.key) }} |
                          {{ $t("id") }} : {{ integrate.value }}
                          {{
                            integrate.number
                              ? " | " + $t("number") + ":" + integrate.number
                              : ""
                          }}
                        </span>
                      </v-chip>
                      <v-chip
                        v-if="item.payed"
                        class="ms-1"
                        :color="
                          parseFloat(item.price.price) - parseFloat(item.payed.amount) > 0
                            ? 'red'
                            : 'green'
                        "
                        small
                        outlined
                        style="cursor: pointer"
                        @click.stop="openPayedDetailsDialog(item)"
                      >
                        {{
                          parseFloat(item.price.price) - parseFloat(item.payed.amount) > 0
                            ? $t("remainPrice") +
                              " " +
                              $numberFormat(
                                parseFloat(item.price.price) -
                                  parseFloat(item.payed.amount)
                              )
                            : $t("payed")
                        }}
                      </v-chip>
                    </div>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="ms-1">
                  <v-btn dark text small color="primary" @click="edit(item)">
                    {{ $numberFormat(item.price ? item.price.NetPrice : 0) }}
                    {{ item.currency ? item.currency.symbol : "" }}
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </td>
          </tr>
        </template>
        <template v-slot:item.action="{ item }">
          <div class="invoice-list-action-cell">
            <v-menu offset-y :z-index="100">
              <template v-slot:activator="{ attrs, on }">
                <v-btn
                  color="primary"
                  class="invoice-list-action-cell__btn"
                  v-bind="attrs"
                  icon
                  x-small
                  v-on="on"
                >
                  <v-icon small> mdi-dots-vertical </v-icon>
                </v-btn>
              </template>

              <v-list dense>
                <v-list-item
                  link
                  :disabled="item.lock ? true : false"
                  @click="edit(item)"
                >
                  <v-list-item-icon>
                    <v-icon>mdi-clipboard-edit</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title v-text="$t('edit')" />
                </v-list-item>
                <v-list-item
                  v-if="lockPermission"
                  link
                  @click="lock(item, item.lock ? 'unlock' : 'lock')"
                >
                  <v-list-item-icon>
                    <v-icon :color="item.lock ? 'green' : 'pink'">
                      {{ item.lock ? "mdi-lock-open-outline" : "mdi-lock-outline" }}
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-title v-text="item.lock ? $t('unlock') : $t('lock')" />
                </v-list-item>
                <v-list-item
                  v-show="type == 'sell' || type == 'presale'"
                  link
                  @click="editCategory(item)"
                >
                  <v-list-item-icon>
                    <v-icon>mdi-clipboard-edit</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title v-text="$t('editCasher')" />
                </v-list-item>
                <v-list-item link @click="viewInvoice(item)">
                  <v-list-item-icon>
                    <v-icon>mdi-eye-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title v-text="$t('invoiceViewOnly')" />
                </v-list-item>
                <v-list-item link @click="printChoice(item)">
                  <v-list-item-icon>
                    <v-icon>mdi-printer-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title v-text="$t('print')" />
                </v-list-item>
                <v-list-item
                  v-if="
                    !documentPermmission &&
                    item.attachments &&
                    item.attachments.length > 0
                  "
                  link
                  @click="openDocumentsDialog(item)"
                >
                  <v-list-item-icon>
                    <v-icon>mdi-attachment</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title v-text="$t('documents')" />
                </v-list-item>
                <v-list-item
                  v-if="type == 'sell' || type == 'presale'"
                  link
                  @click="invoiceSms(item)"
                >
                  <v-list-item-icon>
                    <v-icon>mdi-message-text-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title v-text="$t('sendInvoiceSMS')" />
                </v-list-item>
                <v-list-item v-if="item.entrepot" link @click="entrepotPrint(item)">
                  <v-list-item-icon>
                    <v-icon>mdi-warehouse</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title v-text="$t(item.entrepot.type + 'Print')" />
                </v-list-item>
                <v-list-item link @click="barcodeSingle(item)">
                  <v-list-item-icon>
                    <v-icon>mdi-barcode</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title v-text="$t('barcode')" />
                </v-list-item>
                <log
                  :title-prop="$t('history')"
                  icon-prop="mdi-history"
                  :listProp="true"
                  :type-prop="typeForm"
                  :refIdProp="item.id"
                  mode-prop="button"
                />
                <v-list-item
                  v-if="taxPayerSystem && (type == 'sell' || type == 'returnSell')"
                  link
                  @click="taxPayerConfirmationVMenu(item)"
                >
                  <v-list-item-icon>
                    <v-icon>mdi-cash</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title v-text="$t('send to tax payer system')" />
                </v-list-item>
                <v-list-item link @click="copyInvoice(item)">
                  <v-list-item-icon>
                    <v-icon>mdi-content-copy</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title v-text="$t('invoiceCopy')" />
                </v-list-item>
                <v-list-item
                  v-clipboard:copy="$baseURL + '/' + type + '/edit/' + item.uid"
                  link
                >
                  <v-list-item-icon>
                    <v-icon>mdi-link</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title v-text="$t('copy-link')" />
                </v-list-item>
                <v-list-item
                  v-show="
                    item.tax_payer &&
                    item.tax_payer.taxid &&
                    item.tax_payer.status == 'done'
                  "
                  link
                  :disabled="item.lock ? true : false"
                  @click="editTaxInvoice(item)"
                >
                  <v-list-item-icon>
                    <v-icon color="primary"> mdi-note-edit-outline </v-icon>
                  </v-list-item-icon>
                  <v-list-item-title v-text="$t('sendEditTaxInvoice')" />
                </v-list-item>
                <v-list-item
                  v-show="removePermission(type) && resolveAnnulmentBtnVisibility(item)"
                  link
                  :disabled="item.lock ? true : false"
                  @click="openDeleteDialog(item, 'annulment')"
                >
                  <v-list-item-icon>
                    <v-icon color="pink"> mdi-close-circle-outline </v-icon>
                  </v-list-item-icon>
                  <v-list-item-title v-text="$t('annulmentInvoice')" />
                </v-list-item>
                <v-list-item
                  v-show="removePermission(type) && resolveDeleteBtnVisibility(item)"
                  link
                  :disabled="item.lock ? true : false"
                  @click="openDeleteDialog(item, 'delete')"
                >
                  <v-list-item-icon>
                    <v-icon color="pink"> mdi-trash-can-outline </v-icon>
                  </v-list-item-icon>
                  <v-list-item-title v-text="$t('remove')" />
                </v-list-item>
              </v-list>
            </v-menu>
            <span class="invoice-list-action-cell__row">{{
              parseInt(items.indexOf(item)) + parseInt(from)
            }}</span>
          </div>
        </template>
        <template v-slot:item.id="{ item }">
          <div
            class="invoice-list-icon-cell"
            :class="{ 'invoice-list-icon-cell--has-icon': !!item.lock }"
          >
            <div v-if="item.lock" class="invoice-list-icon-cell__icon">
              <v-icon color="red" class="invoice-list-icon-cell__glyph">
                mdi-lock-outline
              </v-icon>
            </div>
            <div class="invoice-list-icon-cell__body">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on" class="invoice-list-id-line">
                    <div class="invoice-list-two-line">
                      <a @click="edit(item)">
                        {{ item.number != null ? item.number : $t("haveNot") }}
                        {{ item.official_number ? "(" + item.official_number + ")" : "" }}
                      </a>
                      <v-chip
                        v-if="type === 'removedInvoices'"
                        :color="
                          item.type === 'purchase' || item.type === 'returnPurchase'
                            ? 'blue'
                            : 'green'
                        "
                        label
                        x-small
                        class="px-1 ms-1"
                      >
                        <span class="white--text">{{ $t(item.type) }}</span>
                      </v-chip>
                      <span
                        class="invoice-list-two-line__sub text-caption text-disable secondary--text"
                      >
                        #{{ item.id != null ? item.id : $t("haveNot") }}
                      </span>
                    </div>
                  </div>
                </template>
                <span>#{{ item.id != null ? item.id : $t("haveNot") }}</span>
              </v-tooltip>
            </div>
          </div>
        </template>
        <template v-slot:item.doc_header.number="{ item }">
          <div class="invoice-list-doc-cell">
            <a
              v-if="item.doc_header"
              @click="openComponent('accountingPrint', item.doc_head_id)"
              class="text-disable secondary--text"
            >
              {{ item.doc_header.number }}
            </a>
            <span v-else class="text-caption text-disable secondary--text">
              {{ $t("haveNot") }}
            </span>
          </div>
        </template>
        <template v-slot:item.customer.name="{ item }">
          <div class="invoice-list-icon-cell">
            <div class="invoice-list-icon-cell__icon">
              <v-tooltip v-if="item.customer" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    class="invoice-list-icon-cell__btn"
                    icon
                    x-small
                    v-on="on"
                    @click="openComponent('accountingTurnover', item.customer.fin_id)"
                  >
                    <v-icon color="teal" class="invoice-list-icon-cell__glyph">
                      mdi-playlist-check
                    </v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("turnover") }}</span>
              </v-tooltip>
            </div>
            <div class="invoice-list-icon-cell__body">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on" class="invoice-list-customer-line">
                    <div class="invoice-list-two-line">
                      <a
                        class="invoice-list-customer-link"
                        @click="openComponent('account', item.customer_id)"
                      >
                        {{ item.customer ? item.customer.name : $t("haveNot") }}
                      </a>
                      <span
                        class="invoice-list-two-line__sub text-caption text-disable secondary--text"
                      >
                        {{
                          item.customer &&
                          item.customer.mobile != null &&
                          item.customer.mobile != ""
                            ? item.customer.mobile
                            : $t("haveNot")
                        }}
                      </span>
                    </div>
                  </div>
                </template>
                <span>
                  {{
                    item.customer &&
                    item.customer.mobile != null &&
                    item.customer.mobile != ""
                      ? item.customer.mobile
                      : $t("haveNot")
                  }}
                </span>
              </v-tooltip>
            </div>
          </div>
        </template>
        <template v-slot:item.date="{ item }">
          <span
            class="invoice-list-center-cell text-caption text-disable secondary--text"
          >
            {{ item.date ? item.date : $t("haveNot") }}
          </span>
        </template>
        <template v-slot:item.discount="{ item }">
          <span class="invoice-list-amount-cell text-caption">
            {{
              $numberFormat(item.discount || 0, item.currency ? item.currency.decimal : 0)
            }}
            {{ item.currency ? item.currency.symbol : "" }}
          </span>
        </template>
        <template v-slot:item.tax="{ item }">
          <span class="invoice-list-amount-cell text-caption">
            {{ $numberFormat(item.tax || 0, item.currency ? item.currency.decimal : 0) }}
            {{ item.currency ? item.currency.symbol : "" }}
          </span>
        </template>
        <template v-slot:item.price.price="{ item }">
          <span class="invoice-list-amount-cell text-caption">
            {{
              $numberFormat(
                item.price ? item.price.price : 0,
                item.currency ? item.currency.decimal : 0
              )
            }}
            {{ item.currency ? item.currency.symbol : "" }}
          </span>
        </template>
        <template v-slot:item.payed.amount="{ item }">
          <div
            v-if="item.payed == null"
            class="pa-0 text-caption text-disable secondary--text"
          >
            {{ $t("haveNot") }}
          </div>
          <div
            v-else
            class="invoice-list-mini-cell invoice-list-mini-cell--clickable"
            @click.stop="openPayedDetailsDialog(item)"
          >
            <span class="invoice-list-mini-cell__primary">
              {{
                $numberFormat(
                  item.payed.amount || 0,
                  item.currency ? item.currency.decimal : 0
                )
              }}
              {{ item.currency ? item.currency.symbol : "" }}
            </span>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                  class="invoice-list-payed-status"
                  :class="
                    invoiceListPayedIsRemain(item)
                      ? 'invoice-list-payed-status--remain'
                      : 'invoice-list-payed-status--ok'
                  "
                >
                  {{
                    invoiceListPayedIsRemain(item)
                      ? $t("remainPrice") +
                        " " +
                        $numberFormat(invoiceListPayedRemain(item))
                      : $t("payed")
                  }}
                  <v-icon x-small class="ms-1 invoice-list-payed-status__icon">
                    mdi-open-in-new
                  </v-icon>
                </span>
              </template>
              <div class="invoice-list-tooltip-lines">
                <div>
                  {{ $t("cashPay") }}:
                  {{
                    $numberFormat(
                      item.payed.paymentsAmount || 0,
                      item.currency ? item.currency.decimal : 0
                    )
                  }}
                  {{ item.currency ? item.currency.symbol : "" }}
                </div>
                <div>
                  {{ $t("check") }}:
                  {{
                    $numberFormat(
                      item.payed.checksAmount || 0,
                      item.currency ? item.currency.decimal : 0
                    )
                  }}
                  {{ item.currency ? item.currency.symbol : "" }}
                </div>
              </div>
            </v-tooltip>
          </div>
        </template>
        <template v-slot:item.pre_invoice="{ item }">
          <div class="invoice-list-icon-cell">
            <div class="invoice-list-icon-cell__icon">
              <v-icon
                v-if="item.pre_invoice == 'active'"
                color="green"
                class="invoice-list-icon-cell__glyph"
              >
                mdi-check
              </v-icon>
            </div>
            <div class="invoice-list-icon-cell__body">
              <v-card-text
                v-if="item.pre_invoice != 'active'"
                class="pa-0 text-caption text-disable secondary--text"
              >
                ندارد
              </v-card-text>
            </div>
          </div>
        </template>
        <template v-slot:item.entrepot.id="{ item }">
          <v-tooltip v-if="item.entrepot" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                :loading="loading"
                class="ma-0 pa-0 invoice-list-entrepot-btn"
                :color="
                  item.entrepot.type === 'remittance'
                    ? 'pink'
                    : item.entrepot.type === 'receipt'
                    ? 'teal'
                    : 'blue'
                "
                plain
                small
                @click="entrepot_(item.entrepot)"
              >
                <span class="invoice-list-entrepot-btn__text">
                  {{ $t(item.entrepot.type) }}
                  {{
                    item.entrepot.entrepot && item.entrepot.entrepot.name
                      ? item.entrepot.entrepot.name
                      : ""
                  }}
                </span>
                <v-icon x-small class="ms-1">mdi-open-in-new</v-icon>
              </v-btn>
            </template>
            <div class="invoice-list-tooltip-lines">
              <div>
                {{ $t(item.entrepot.type) }} {{ $t("entrepot") }}
                {{
                  item.entrepot.entrepot && item.entrepot.entrepot.name
                    ? item.entrepot.entrepot.name
                    : ""
                }}
              </div>
              <div>{{ $t("id") }}: {{ item.entrepot.id }}</div>
            </div>
          </v-tooltip>
          <v-card-text v-else class="pa-0 text-caption text-disable secondary--text">{{
            $t("haveNot")
          }}</v-card-text>
        </template>
        <template v-slot:item.branch.name="{ item }">
          <span
            class="invoice-list-center-cell text-caption"
            :class="{ 'text-disable secondary--text': !item.branch }"
          >
            {{ item.branch ? item.branch.name : $t("haveNot") }}
          </span>
        </template>
        <template v-slot:item.last_status.status_id="{ item }">
          <v-tooltip v-if="item.last_status && item.last_status.status" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                v-bind="attrs"
                v-on="on"
                :color="item.last_status.status.color"
                label
                small
                style="cursor: pointer"
                class="px-1 invoice-list-status-chip"
              >
                <span class="px-2 white--text invoice-list-status-chip__text">
                  {{ item.last_status.status.name }}
                </span>
              </v-chip>
            </template>
            <span>{{ item.last_status.status.name }}</span>
          </v-tooltip>
          <v-card-text v-else class="pa-0 text-caption text-disable secondary--text">{{
            $t("haveNot")
          }}</v-card-text>
        </template>
        <template v-slot:item.due_date="{ item }">
          <span
            class="invoice-list-center-cell text-caption text-disable secondary--text"
          >
            {{ item.due_date ? item.due_date : "" }}
          </span>
        </template>
        <template v-slot:item.tags="{ item }">
          <pl-tags :tags="item.tags || []" />
        </template>
        <template v-slot:item.taxid="{ item }">
          <v-chip
            v-if="item.tax_payer == null || item.tax_payer == undefined"
            small
            color="secondary"
          >
            {{ resolveSubmitionDeadline(item.date) }}
          </v-chip>
          <v-card-text
            v-if="item.tax_payer && item.tax_payer.taxid"
            class="pa-0 text-caption text-disable secondary--text"
          >
            {{ item.tax_payer.taxid }}
          </v-card-text>
          <v-card-text
            v-if="item.tax_payer && item.tax_payer.taxid == null"
            class="pa-0 text-caption text-disable secondary--text"
          >
            {{ $t("haveNot") }}
          </v-card-text>
        </template>
        <template v-slot:item.system_status="{ item }">
          <v-chip
            v-if="
              item.tax_payer &&
              item.tax_payer.status != 'error' &&
              item.tax_payer.status != 'sent' &&
              item.tax_payer.status != 'done' &&
              item.tax_payer.status != 'editSent' &&
              item.tax_payer.status != 'removeSent'
            "
            small
            :color="resolveTaxPayerStatusColor(item.tax_payer)"
            @click="openTaxPayerStatus(item.tax_payer)"
          >
            {{ resolveTaxPayerStatus(item.tax_payer) }}
          </v-chip>
          <v-chip
            v-if="
              item.tax_payer &&
              (item.tax_payer.status == 'error' ||
                item.tax_payer.status == 'sent' ||
                item.tax_payer.status == 'done' ||
                item.tax_payer.status == 'editSent' ||
                item.tax_payer.status == 'removeSent')
            "
            small
            :color="
              item.tax_payer.status == 'error'
                ? 'error'
                : item.tax_payer.status == 'sent'
                ? 'primary'
                : item.tax_payer.status == 'done'
                ? 'success'
                : item.tax_payer.status == 'editSent'
                ? 'warning'
                : item.tax_payer.status == 'removeSent'
                ? 'error'
                : ''
            "
          >
            {{
              item.tax_payer.status == "error"
                ? $t("error-in-sending-invoice")
                : item.tax_payer.status == "sent"
                ? $t("shipped")
                : item.tax_payer.status == "done"
                ? $t("confirmed")
                : item.tax_payer.status == "editSent"
                ? $t("edited")
                : item.tax_payer.status == "removeSent"
                ? $t("cenceled")
                : $t("undefined")
            }}
          </v-chip>
        </template>
        <template v-slot:item.content="{ item }">
          <!-- Desktop dense: like classic — logo+id / 20-char text in cell; full text on hover -->
          <div v-if="isDesktopListViewport" class="invoice-list-other-compact">
            <span
              v-if="!invoiceListHasOtherContent(item)"
              class="text-caption text-disable secondary--text"
            >
              {{ $t("haveNot") }}
            </span>
            <div v-else class="invoice-list-other-compact__body">
              <div
                v-for="(row, idx) in invoiceListOtherDetailRows(item).slice(0, 2)"
                :key="'other-inline-' + idx"
                class="invoice-list-other-compact__row"
              >
                <img
                  v-if="row.img"
                  :src="row.img"
                  class="invoice-list-other-compact__logo"
                  alt=""
                />
                <span class="invoice-list-other-compact__label">{{ row.label }}</span>
                <span
                  v-if="row.copy"
                  v-clipboard:copy="row.copy"
                  class="invoice-list-other-compact__value"
                  style="cursor: pointer"
                  @click="copied"
                >
                  {{ row.value }}
                </span>
                <v-icon
                  v-if="row.copy && row.channel"
                  x-small
                  class="ms-1"
                  style="cursor: pointer"
                  @click.stop="openIntegratorChannel(row)"
                >
                  mdi-trending-up
                </v-icon>
                <span v-else class="invoice-list-other-compact__value">{{
                  row.value
                }}</span>
              </div>
              <v-tooltip
                v-if="item.content"
                top
                max-width="360"
                content-class="invoice-list-other-tooltip"
              >
                <template v-slot:activator="{ on, attrs }">
                  <span
                    class="invoice-list-other-compact__content text-caption text-disable secondary--text"
                    v-bind="attrs"
                    v-on="on"
                  >
                    {{
                      item.deleted_at
                        ? item.content
                        : item.content.length > 20
                        ? item.content.substr(0, 20) + "..."
                        : item.content
                    }}
                  </span>
                </template>
                <span>{{ item.content }}</span>
              </v-tooltip>
              <v-tooltip
                v-if="item.custom_fields && item.custom_fields.length"
                top
                max-width="360"
                content-class="invoice-list-other-tooltip"
              >
                <template v-slot:activator="{ on, attrs }">
                  <span
                    class="invoice-list-other-compact__fields text-caption"
                    v-bind="attrs"
                    v-on="on"
                  >
                    {{ item.custom_fields.length }} {{ $t("details") }}
                  </span>
                </template>
                <div>
                  <div
                    v-for="(field, index) in item.custom_fields"
                    :key="'cf-tip-' + index"
                  >
                    {{ field.key }} : {{ field.value }}
                  </div>
                </div>
              </v-tooltip>
            </div>
          </div>
          <div v-else>
            <v-card-text
              v-if="
                item.integrator.length == 0 ||
                (item.integrator.length != 0 &&
                  !item.integrator.find((item) => item.key == 'woocommerceInvoice') &&
                  item.integrator.length != 0 &&
                  !item.integrator.find((item) => item.key == 'digikalaInvoice') &&
                  item.integrator.length != 0 &&
                  !item.integrator.find((item) => item.key == 'sazitoInvoice') &&
                  item.integrator.length != 0 &&
                  !item.integrator.find((item) => item.key == 'webziInvoice') &&
                  item.integrator.length != 0 &&
                  !item.integrator.find((item) => item.key == 'portalInvoice') &&
                  item.integrator.length != 0 &&
                  !item.integrator.find((item) => item.key == 'basalamInvoice') &&
                  item.integrator.length != 0 &&
                  !item.integrator.find((item) => item.key == 'mixinInvoice') &&
                  item.integrator.length != 0 &&
                  !item.integrator.find((item) => item.key == 'magnetInvoice') &&
                  item.integrator.length != 0 &&
                  !item.integrator.find((item) => item.key == 'snapshopInvoice') &&
                  item.content == null &&
                  item.custom_fields.length == 0)
              "
              class="pa-0 text-caption text-disable secondary--text"
            >
              {{ $t("haveNot") }}
            </v-card-text>
            <!--woocommerce id-->
            <v-container
              class="pa-0 invoice-list-icon-cell invoice-list-icon-cell--inline"
              v-if="
                item.integrator.length > 0 &&
                item.integrator.find((item) => item.key == 'woocommerceInvoice') &&
                item.integrator.find((item) => item.key == 'woocommerceInvoice').value !=
                  null
              "
            >
              <div class="invoice-list-icon-cell__icon">
                <img src="@/assets/woo.png" class="invoice-list-icon-cell__img" alt="" />
              </div>
              <div class="invoice-list-icon-cell__body invoice-list-icon-cell__body--row">
                <v-card-text class="pa-0 text-caption">{{ $t("id") }}: </v-card-text>
                <v-card-text
                  v-if="
                    item.integrator.length > 0 &&
                    item.integrator.find((item) => item.key == 'woocommerceInvoice') &&
                    item.integrator.find((item) => item.key == 'woocommerceInvoice')
                      .value != null
                  "
                  v-clipboard:copy="
                    item.integrator.find((item) => item.key == 'woocommerceInvoice').value
                  "
                  @click="copied"
                  class="pa-0 text-caption text-disable secondary--text"
                  style="cursor: pointer"
                >
                  {{
                    item.integrator.find((item) => item.key == "woocommerceInvoice").value
                  }}
                </v-card-text>
              </div>
            </v-container>
            <!--digikala id-->
            <v-container
              v-if="
                item.integrator &&
                item.integrator.some((i) => i.key === 'digikalaInvoice' && i.value)
              "
              class="pa-0 d-flex flex-column gap-1"
            >
              <v-container
                v-for="(dg, index) in item.integrator.filter(
                  (i) => i.key === 'digikalaInvoice' && i.value
                )"
                :key="index"
                class="pa-0 invoice-list-icon-cell invoice-list-icon-cell--inline"
              >
                <div class="invoice-list-icon-cell__icon">
                  <img
                    src="@/assets/digikala.png"
                    class="invoice-list-icon-cell__img"
                    alt=""
                  />
                </div>
                <div
                  class="invoice-list-icon-cell__body invoice-list-icon-cell__body--row"
                >
                  <v-card-text class="pa-0 text-caption">
                    {{ $t("id") }}:
                    <span v-if="dg.panel != null"> ({{ dg.panel.abr_name }}) </span>
                  </v-card-text>
                  <v-card-text
                    v-clipboard:copy="dg.value"
                    @click="copied"
                    class="pa-0 text-caption text-disable secondary--text"
                    style="cursor: pointer"
                  >
                    {{ dg.value }}
                  </v-card-text>
                </div>
              </v-container>
            </v-container>
            <!--basalam id-->
            <v-container
              v-if="
                item.integrator &&
                item.integrator.some((i) => i.key === 'basalamInvoice' && i.value)
              "
              class="pa-0 d-flex flex-column gap-1"
            >
              <v-container
                v-for="(basalam, index) in item.integrator.filter(
                  (i) => i.key === 'basalamInvoice' && i.value
                )"
                :key="index"
                class="pa-0 invoice-list-icon-cell invoice-list-icon-cell--inline"
              >
                <div class="invoice-list-icon-cell__icon">
                  <img
                    src="@/assets/basalam.png"
                    class="invoice-list-icon-cell__img"
                    alt=""
                  />
                </div>
                <div
                  class="invoice-list-icon-cell__body invoice-list-icon-cell__body--row"
                >
                  <v-card-text class="pa-0 text-caption">
                    {{ $t("id") }}:
                    <span v-if="basalam.panel != null">
                      ({{ basalam.panel.abr_name }})
                    </span>
                  </v-card-text>
                  <v-card-text
                    v-clipboard:copy="basalam.value"
                    @click="copied"
                    class="pa-0 text-caption text-disable secondary--text"
                    style="cursor: pointer"
                  >
                    {{ basalam.value }}
                  </v-card-text>
                </div>
              </v-container>
            </v-container>
            <!--sazito id-->
            <v-container
              v-if="
                item.integrator &&
                item.integrator.some((i) => i.key === 'sazitoInvoice' && i.value)
              "
              class="pa-0 d-flex flex-column gap-1"
            >
              <v-container
                v-for="(sz, index) in item.integrator.filter(
                  (i) => i.key === 'sazitoInvoice' && i.value
                )"
                :key="index"
                class="pa-0 invoice-list-icon-cell invoice-list-icon-cell--inline"
              >
                <div class="invoice-list-icon-cell__icon">
                  <img
                    src="@/assets/sazito(1).png"
                    class="invoice-list-icon-cell__img"
                    alt=""
                  />
                </div>
                <div
                  class="invoice-list-icon-cell__body invoice-list-icon-cell__body--row"
                >
                  <v-card-text class="pa-0 text-caption">
                    {{ $t("id") }}:
                    <span v-if="sz.panel != null"> ({{ sz.panel.abr_name }}) </span>
                  </v-card-text>
                  <v-card-text
                    v-clipboard:copy="sz.value"
                    @click="copied"
                    class="pa-0 text-caption text-disable secondary--text"
                    style="cursor: pointer"
                  >
                    {{ sz.value }}
                  </v-card-text>
                </div>
              </v-container>
            </v-container>
            <!--magnet id-->
            <v-container
              v-if="
                item.integrator &&
                item.integrator.some((i) => i.key === 'magnetInvoice' && i.value)
              "
              class="pa-0 d-flex flex-column gap-1"
            >
              <v-container
                v-for="(mg, index) in item.integrator.filter(
                  (i) => i.key === 'magnetInvoice' && i.value
                )"
                :key="index"
                class="pa-0 invoice-list-icon-cell invoice-list-icon-cell--inline"
              >
                <div class="invoice-list-icon-cell__icon">
                  <img
                    src="@/assets/integrators/logo-magnet.png"
                    class="invoice-list-icon-cell__img"
                    alt=""
                  />
                </div>
                <div
                  class="invoice-list-icon-cell__body invoice-list-icon-cell__body--row"
                >
                  <v-card-text class="pa-0 text-caption">
                    {{ $t("id") }}:
                    <span v-if="mg.panel != null"> ({{ mg.panel.abr_name }}) </span>
                  </v-card-text>
                  <v-card-text
                    v-clipboard:copy="mg.value"
                    @click="copied"
                    class="pa-0 text-caption text-disable secondary--text"
                    style="cursor: pointer"
                  >
                    {{ mg.value }}
                  </v-card-text>
                </div>
              </v-container>
            </v-container>
            <!--mixin id-->
            <v-container
              v-if="
                item.integrator &&
                item.integrator.some((i) => i.key === 'mixinInvoice' && i.value)
              "
              class="pa-0 d-flex flex-column gap-1"
            >
              <v-container
                v-for="(mix, index) in item.integrator.filter(
                  (i) => i.key === 'mixinInvoice' && i.value
                )"
                :key="index"
                class="pa-0 invoice-list-icon-cell invoice-list-icon-cell--inline"
              >
                <div class="invoice-list-icon-cell__icon">
                  <img
                    src="@/assets/integrators/mixin.png"
                    class="invoice-list-icon-cell__img"
                    alt=""
                  />
                </div>
                <div
                  class="invoice-list-icon-cell__body invoice-list-icon-cell__body--row"
                >
                  <v-card-text class="pa-0 text-caption">
                    {{ $t("id") }}:
                    <span v-if="mix.panel != null"> ({{ mix.panel.abr_name }}) </span>
                  </v-card-text>
                  <v-card-text
                    v-clipboard:copy="mix.value"
                    @click="copied"
                    class="pa-0 text-caption text-disable secondary--text"
                    style="cursor: pointer"
                  >
                    {{ mix.value }}
                  </v-card-text>
                </div>
              </v-container>
            </v-container>
            <!--snapshop id-->
            <v-container
              v-if="
                item.integrator &&
                item.integrator.some((i) => i.key === 'snapshopInvoice' && i.value)
              "
              class="pa-0 d-flex flex-column gap-1"
            >
              <v-container
                v-for="(snap, index) in item.integrator.filter(
                  (i) => i.key === 'snapshopInvoice' && i.value
                )"
                :key="index"
                class="pa-0 invoice-list-icon-cell invoice-list-icon-cell--inline"
              >
                <div class="invoice-list-icon-cell__icon">
                  <img
                    src="@/assets/integrators/snapShop.png"
                    class="invoice-list-icon-cell__img"
                    alt=""
                  />
                </div>
                <div
                  class="invoice-list-icon-cell__body invoice-list-icon-cell__body--row"
                >
                  <v-card-text class="pa-0 text-caption">
                    {{ $t("id") }}:
                    <span v-if="snap.panel != null"> ({{ snap.panel.abr_name }}) </span>
                  </v-card-text>
                  <v-card-text
                    v-clipboard:copy="snap.value"
                    @click="copied"
                    class="pa-0 text-caption text-disable secondary--text"
                    style="cursor: pointer"
                  >
                    {{ snap.value }}
                  </v-card-text>
                </div>
              </v-container>
            </v-container>
            <!--webzi id-->
            <v-container
              class="pa-0 invoice-list-icon-cell invoice-list-icon-cell--inline"
              v-if="
                item.integrator.length > 0 &&
                item.integrator.find((item) => item.key == 'webziInvoice') &&
                item.integrator.find((item) => item.key == 'webziInvoice').value != null
              "
            >
              <div class="invoice-list-icon-cell__icon">
                <img
                  src="@/assets/webzi.png"
                  class="invoice-list-icon-cell__img"
                  alt=""
                />
              </div>
              <div class="invoice-list-icon-cell__body invoice-list-icon-cell__body--row">
                <v-card-text class="pa-0 text-caption">{{ $t("id") }}: </v-card-text>
                <v-card-text
                  v-if="
                    item.integrator.length > 0 &&
                    item.integrator.find((item) => item.key == 'webziInvoice') &&
                    item.integrator.find((item) => item.key == 'webziInvoice').value !=
                      null
                  "
                  v-clipboard:copy="
                    item.integrator.find((item) => item.key == 'webziInvoice').value
                  "
                  @click="copied"
                  class="pa-0 text-caption text-disable secondary--text"
                  style="cursor: pointer"
                >
                  {{ item.integrator.find((item) => item.key == "webziInvoice").value }}
                </v-card-text>
              </div>
            </v-container>
            <!--markito id-->
            <v-container
              class="pa-0 invoice-list-icon-cell invoice-list-icon-cell--inline"
              v-if="
                item.integrator.length > 0 &&
                item.integrator.find((item) => item.key == 'markitoInvoice') &&
                item.integrator.find((item) => item.key == 'markitoInvoice').value != null
              "
            >
              <div class="invoice-list-icon-cell__icon">
                <img
                  src="@/assets/markito.png"
                  class="invoice-list-icon-cell__img"
                  alt=""
                />
              </div>
              <div class="invoice-list-icon-cell__body invoice-list-icon-cell__body--row">
                <v-card-text class="pa-0 text-caption">{{ $t("id") }}: </v-card-text>
                <v-card-text
                  v-if="
                    item.integrator.length > 0 &&
                    item.integrator.find((item) => item.key == 'markitoInvoice') &&
                    item.integrator.find((item) => item.key == 'markitoInvoice').value !=
                      null
                  "
                  v-clipboard:copy="
                    item.integrator.find((item) => item.key == 'markitoInvoice').value
                  "
                  @click="copied"
                  class="pa-0 text-caption text-disable secondary--text"
                  style="cursor: pointer"
                >
                  {{ item.integrator.find((item) => item.key == "markitoInvoice").value }}
                </v-card-text>
              </div>
            </v-container>
            <!--portal id-->
            <v-container
              class="pa-0 invoice-list-icon-cell invoice-list-icon-cell--inline"
              v-if="
                item.integrator.length > 0 &&
                item.integrator.find((item) => item.key == 'portalInvoice') &&
                item.integrator.find((item) => item.key == 'portalInvoice').value != null
              "
            >
              <div class="invoice-list-icon-cell__icon">
                <img
                  src="@/assets/portal.png"
                  class="invoice-list-icon-cell__img"
                  alt=""
                />
              </div>
              <div class="invoice-list-icon-cell__body invoice-list-icon-cell__body--row">
                <v-card-text class="pa-0 text-caption">{{ $t("id") }}: </v-card-text>
                <v-card-text
                  v-if="
                    item.integrator.length > 0 &&
                    item.integrator.find((item) => item.key == 'portalInvoice') &&
                    item.integrator.find((item) => item.key == 'portalInvoice').value !=
                      null
                  "
                  v-clipboard:copy="
                    item.integrator.find((item) => item.key == 'portalInvoice').value
                  "
                  @click="copied"
                  class="pa-0 text-caption text-disable secondary--text"
                  style="cursor: pointer"
                >
                  {{ item.integrator.find((item) => item.key == "portalInvoice").value }}
                </v-card-text>
              </div>
            </v-container>
            <!--custom filds-->
            <div v-if="item.custom_fields" class="pt-1">
              <v-chip
                v-for="(field, index) in item.custom_fields"
                :key="index"
                label
                color="primary"
                small
                style="margin-left: 1px; margin-right: 1px"
                class="px-1 mb-1"
              >
                <span class="px-1 white--text">
                  {{ field.key }} : {{ field.value }}
                </span>
              </v-chip>
            </div>
            <v-card-text class="pa-0 text-caption text-disable secondary--text">
              {{
                item.content
                  ? item.content.substr(0, item.deleted_at ? item.content.length : 20)
                  : ""
              }}
              {{
                !item.deleted_at && item.content && item.content.length > 20 ? "..." : ""
              }}
            </v-card-text>
          </div>
        </template>
        <template v-slot:item.user.name="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                v-on="on"
                class="invoice-list-center-cell text-caption"
                :class="{ 'text-disable secondary--text': !item.user }"
              >
                <template v-if="item.user">
                  {{ item.user.firstname }} {{ item.user.lastname }}
                </template>
                <template v-else>{{ $t("haveNot") }}</template>
              </span>
            </template>
            <span>{{ item.time }}</span>
          </v-tooltip>
        </template>
        <!-- <template v-slot:item.price.NetPrice="{ item }">
          <v-card-text class="pa-0 text-caption text-disable secondary--text">
            {{ $numberFormat((item.price ? item.price.NetPrice : 0)) }} {{ item.currency.symbol }}
          </v-card-text>
        </template>
        <template v-slot:item.plan="{ item }">
          <div class="pt-1">
            <v-chip
              v-if="item.plan"
              color="primary"
              label
              small
              style="margin-left: 1px;margin-right:1px"
              class="px-1 mb-1"
            >
              <span class="px-1 white--text">
                {{ (item.plan ? item.plan.plan.name : '') }}
              </span>
            </v-chip>
          </div>
        </template>
        <template v-slot:item.time="{ item }">
          {{ item.time }}
        </template>-->
        <!-- Mobile / embedded: keep page totals inside table body -->
        <template v-slot:body.append>
          <tr v-if="!isDesktopListViewport">
            <td :colspan="`${isMobile ? 6 : Number(showHeaders.length)}`">
              <div style="width: 100%" class="pa-1">
                <strong>{{ $t("sumInPage") }}</strong
                >:
                <v-chip color="pOrrange" class="px-1 me-1" outlined>
                  <span class="px-1" style="font-size: 11px">
                    {{ $t("netAmount") }}:{{
                      $numberFormat(totals.price.toFixed($store.state.currency.decimal))
                    }}
                  </span>
                </v-chip>
                <v-chip color="pink" class="px-1 me-1" outlined>
                  <span class="px-1" style="font-size: 11px">
                    {{ $t("discount") }}:{{
                      $numberFormat(
                        totals.discount.toFixed($store.state.currency.decimal)
                      )
                    }}
                  </span>
                </v-chip>
                <v-chip color="secondary" class="px-1 me-1" outlined>
                  <span class="px-1" style="font-size: 11px">
                    {{ $t("tax") }}:{{
                      $numberFormat(totals.tax.toFixed($store.state.currency.decimal))
                    }}
                  </span>
                </v-chip>
                <v-chip v-if="hasCustomerClub" color="teal" class="px-1 me-1" outlined>
                  <span class="px-1" style="font-size: 11px">
                    {{ $t("club-credit") }}:{{
                      $numberFormat(totals.credit.toFixed($store.state.currency.decimal))
                    }}
                  </span>
                </v-chip>
                <v-chip color="primary" class="px-1 me-1">
                  <span class="px-1 white--text" style="font-size: 11px">
                    {{ $t("clearAmount") }}:{{
                      $numberFormat(
                        totals.sumNetAmount.toFixed($store.state.currency.decimal)
                      )
                    }}
                  </span>
                </v-chip>
              </div>
            </td>
          </tr>
        </template>
        <template v-slot:footer.page-text="{ pageStart, pageStop, itemsLength }">
          {{ faNum(pageStart) }}–{{ faNum(pageStop) }} {{ $t("of") }} {{ faNum($numberFormat(itemsLength)) }}
        </template>
        <template v-slot:footer.prepend>
          <!-- Desktop fixed viewport: page totals sit above pagination, outside body scroll -->
          <div
            v-if="isDesktopListViewport"
            ref="listPageTotals"
            class="invoice-list-page-totals"
          >
            <span class="invoice-list-page-totals__label">{{ $t("sumInPage") }}</span>
            <span class="invoice-list-page-totals__item">
              <span class="invoice-list-page-totals__key">{{ $t("netAmount") }}</span>
              <span class="invoice-list-page-totals__val">
                {{ $numberFormat(totals.price.toFixed($store.state.currency.decimal)) }}
                {{ $store.state.currency.name }}
              </span>
            </span>
            <span class="invoice-list-page-totals__item">
              <span class="invoice-list-page-totals__key">{{ $t("discount") }}</span>
              <span class="invoice-list-page-totals__val">
                {{
                  $numberFormat(totals.discount.toFixed($store.state.currency.decimal))
                }}
                {{ $store.state.currency.name }}
              </span>
            </span>
            <span class="invoice-list-page-totals__item">
              <span class="invoice-list-page-totals__key">{{ $t("tax") }}</span>
              <span class="invoice-list-page-totals__val">
                {{ $numberFormat(totals.tax.toFixed($store.state.currency.decimal)) }}
                {{ $store.state.currency.name }}
              </span>
            </span>
            <span v-if="hasCustomerClub" class="invoice-list-page-totals__item">
              <span class="invoice-list-page-totals__key">{{ $t("club-credit") }}</span>
              <span class="invoice-list-page-totals__val">
                {{ $numberFormat(totals.credit.toFixed($store.state.currency.decimal)) }}
                {{ $store.state.currency.name }}
              </span>
            </span>
            <span
              class="invoice-list-page-totals__item invoice-list-page-totals__item--final"
            >
              <span class="invoice-list-page-totals__key">{{ $t("clearAmount") }}</span>
              <span class="invoice-list-page-totals__val">
                {{
                  $numberFormat(
                    totals.sumNetAmount.toFixed($store.state.currency.decimal)
                  )
                }}
                {{ $store.state.currency.name }}
              </span>
            </span>
          </div>
          <div class="pl-goto">
            <span>{{ $t("goToPage") }}</span>
            <input v-model.number="page" type="number" min="1" :max="pageCount" class="pl-goto__input pl-num" />
            <span class="pl-num">/ {{ faNum(pageCount) }}</span>
          </div>
        </template>
      </v-data-table>
    <dialog-component
      :dialog-prop="innerComponent"
      :data-prop="dialogData"
      @close="innerComponent = false"
    />
    <print-studio
      v-model="printStudio"
      :report="plReportKey"
      :title="typeProp ? '' : $t($route.name)"
      :columns="plPrintCols"
      :source="plPrintSource"
      @error="serverErrorHandeler"
    />
  </pro-list-frame>
</template>

<script>
export default {
  name: "InvoiceListDesktopView",
};
</script>
